import { useEffect } from "react";
import {
  AutocompleteInput,
  maxLength,
  minLength,
  ReferenceInput,
  required,
  TextInput,
  useDataProvider,
  useTranslate,
} from "react-admin";
import MuiDatePickerInput from "../../common/MuiDatePickerInput";
import { useWatch } from "react-hook-form";
import { useMutation } from "react-query";
import Grid from "@mui/material/Grid";
import PriceItemForm from "../create/components/PriceItemForm";
import CodePickers from "./CodePickers";
import {
  filterToQueryKind,
  inputTextKind,
  matchSuggestionKind,
  OptionRendererKind,
} from "../../common/OptionRendererKind";

const greaterThan = (attr) => (value, allValues) => {
  if (!allValues[attr] || allValues[attr] === "0001-01-01T00:00:00Z")
    return undefined;
  if (parseFloat(value) < parseFloat(allValues[attr])) {
    return "Must be greater than " + allValues[attr];
  }
  return undefined;
};
const lessThan = (attr) => (value, allValues) => {
  if (!allValues[attr] || allValues[attr] === "0001-01-01T00:00:00Z")
    return undefined;
  if (parseFloat(value) > parseFloat(allValues[attr])) {
    return "Must be less than " + allValues[attr];
  }
  return undefined;
};

const validateStartDate = [
  required(),
  minLength(10),
  maxLength(10),
  lessThan("end_date"),
];
const validateEndDate = [
  required(),
  minLength(10),
  maxLength(10),
  greaterThan("start_date"),
];
const validateCode50 = [maxLength(50)];
const validateCode10 = [maxLength(10)];

const DynamicCode = ({ dependOn }) => {
  const t = useTranslate();
  const dataProvider = useDataProvider();
  const dependOnKind = useWatch({ name: dependOn });
  const { data, mutate, isLoading } = useMutation((prm) => {
    return dataProvider.getListNoParams("kinds/" + prm);
  });
  useEffect(() => {
    if (dependOnKind) {
      mutate(encodeURIComponent(dependOnKind));
    }
  }, [dependOnKind]);

  if (!dependOnKind || isLoading || !data) {
    return null;
  }

  return data.dynamic_code ? (
    <>
      <ReferenceInput
        source="code"
        reference="products"
        perPage={10}
        sort={{ field: "products.created_at", order: "DESC" }}
        filter={{ "Kind.dynamic_code": true, "Kind.id": dependOnKind }}
      >
        <AutocompleteInput
          label="mine.label.product"
          variant="outlined"
          fullWidth
          sx={{ display: "inline-flex" }}
          validate={validateCode50}
          optionValue="id"
          optionText="code"
          //   optionText={<OptionRendererKind />}
          //   inputText={inputTextKind}
          //   matchSuggestion={matchSuggestionKind}
          filterToQuery={(searchText) => ({
            "products.code": searchText,
          })}
        />
      </ReferenceInput>

      <Grid
        container
        spacing={2}
        sx={{
          "& .MuiGrid-item": { pt: 0 },
          mt: 0,
        }}
      >
        <Grid item xs={12} lg={6}>
          <MuiDatePickerInput
            variant="outlined"
            source="start_date"
            label="mine.label.start_date"
            fullWidth
            validate={validateStartDate}
            maxDateSource="end_date"
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <MuiDatePickerInput
            variant="outlined"
            source="end_date"
            label="mine.label.end_date"
            fullWidth
            validate={validateEndDate}
            minDateSource="start_date"
          />
        </Grid>
      </Grid>

      <PriceItemForm onlyPrices />
    </>
  ) : (
    <>
      <TextInput
        variant="outlined"
        source="code"
        label="mine.label.code"
        fullWidth
        resettable
        validate={validateCode10}
        helperText={t("mine.label.10_chars")}
      />

      <CodePickers dependOn="code" />

      <Grid
        container
        spacing={2}
        sx={{
          "& .MuiGrid-item": { pt: 0 },
          mt: 0,
        }}
      >
        <Grid item xs={12} lg={6}>
          <MuiDatePickerInput
            variant="outlined"
            source="start_date"
            label="mine.label.start_date"
            fullWidth
            validate={validateStartDate}
            maxDateSource="end_date"
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <MuiDatePickerInput
            variant="outlined"
            source="end_date"
            label="mine.label.end_date"
            fullWidth
            validate={validateEndDate}
            minDateSource="start_date"
          />
        </Grid>
      </Grid>

      <PriceItemForm />
    </>
  );
};

export default DynamicCode;
